import { Node } from '@xyflow/react';
import {
  ConfigurationMapEdge,
  ConfigurationMapField,
  ConfigurationMapHook,
  ConfigurationMapRule,
} from '@rossum/api-client/internal';

export const COLUMN_WIDTH = 360;
export const ROW_HEIGHT = 52;
export const NODE_WIDTH = 260;
export const NODE_HEIGHT = 40;
export const HOOK_NODE_HEIGHT = 80;
/** Vertical gap between the hook band and the field band. */
const BAND_GAP = 80;

export type FieldNodeData = {
  label: string;
  fieldType: ConfigurationMapField['type'];
  dependencies: string[];
  nodeKind: 'field';
};

export type RuleNodeData = {
  label: string;
  ruleId: number;
  enabled: boolean;
  dependencies: string[];
  nodeKind: 'rule';
};

export type HookNodeData = {
  label: string;
  hookId: number;
  hookType: ConfigurationMapHook['hookType'];
  active: boolean;
  dependencies: string[];
  targets: string[];
  nodeKind: 'hook';
};

export type ConfigMapNodeData = FieldNodeData | RuleNodeData | HookNodeData;

/**
 * Compute topological depth for each node using Bellman-Ford relaxation.
 * Returns react-flow nodes positioned in depth-based columns.
 *
 * Layout uses two horizontal bands:
 *  - Top band: hook nodes (bottom-aligned so connected hooks sit near fields)
 *  - Bottom band: field nodes (separated by BAND_GAP)
 * Rules are pure sinks placed in a dedicated rightmost column spanning
 * both bands.
 *
 * Cycle handling: Bellman-Ford longest-path only converges on a DAG, so any
 * cycle is removed from the depth graph before relaxation (the edges are still
 * rendered). Two layers of protection:
 *  1. The hook↔field write-back 2-cycle is excluded explicitly so the hook
 *     keeps its orientation to the right of the field it reads.
 *  2. `findBackEdges` removes every remaining cycle (field↔field formula loops,
 *     multi-hop hook/field loops) by dropping DFS back-edges.
 * Without this, depths grow without bound and push the rightmost columns
 * (rules + deepest fields/hooks) far outside the viewport.
 */
export type LayoutResult = {
  nodes: Node<ConfigMapNodeData>[];
  fieldBandY: number;
};

/**
 * Identify back-edges via a depth-first traversal. A back-edge points to a node
 * still on the current DFS stack (an ancestor), so it closes a cycle; dropping
 * all back-edges yields a DAG. Returns the set of `${source}:${target}` keys to
 * exclude from depth computation.
 *
 * Uses local mutation (adjacency list, visit state) — permitted for graph
 * algorithms where a functional rebuild would be O(n²).
 */
const findBackEdges = (
  edges: ConfigurationMapEdge[],
  nodeIds: Set<string>
): Set<string> => {
  const adjacency = new Map<string, string[]>();
  edges.forEach(edge => {
    const targets = adjacency.get(edge.source);
    if (targets) targets.push(edge.target);
    else adjacency.set(edge.source, [edge.target]);
  });

  const VISITING = 1;
  const DONE = 2;
  const state = new Map<string, number>();
  const backEdges = new Set<string>();

  const visit = (node: string): void => {
    state.set(node, VISITING);
    (adjacency.get(node) ?? []).forEach(next => {
      const nextState = state.get(next);
      if (nextState === VISITING) {
        // `next` is an ancestor on the current path — this edge closes a cycle.
        backEdges.add(`${node}:${next}`);
      } else if (nextState === undefined) {
        visit(next);
      }
      // DONE → already fully explored; a safe forward/cross edge.
    });
    state.set(node, DONE);
  };

  Array.from(nodeIds).forEach(node => {
    if (!state.has(node)) visit(node);
  });

  return backEdges;
};

export const computeLayout = (
  fields: ConfigurationMapField[],
  graphEdges: ConfigurationMapEdge[],
  rules: ConfigurationMapRule[],
  hooks: ConfigurationMapHook[]
): LayoutResult => {
  const fieldMap = new Map(fields.map(f => [f.id, f]));
  const ruleMap = new Map(rules.map(r => [r.id, r]));
  const hookMap = new Map(hooks.map(h => [h.id, h]));

  const ruleIds = new Set(ruleMap.keys());
  const hookIds = new Set(hookMap.keys());

  // Collect field→hook edges as "source:target" strings. When a hook→field
  // edge exists for the same pair (reversed lookup "target:source"), it forms
  // a simple cycle that would prevent Bellman-Ford from converging. We exclude
  // such back-edges from depth computation while still rendering them.
  const hookInputPairs = new Set(
    graphEdges
      .filter(e => hookIds.has(e.target) && !hookIds.has(e.source))
      .map(e => `${e.source}:${e.target}`)
  );

  // Edges for depth computation:
  //  - Exclude rule edges (rules are placed separately after all other nodes)
  //  - Exclude hook→field back-edges that complete a cycle (reverse lookup)
  const depthEdges = graphEdges.filter(e => {
    if (ruleIds.has(e.source) || ruleIds.has(e.target)) return false;
    if (hookIds.has(e.source) && !hookIds.has(e.target)) {
      return !hookInputPairs.has(`${e.target}:${e.source}`);
    }
    return true;
  });

  const allConnectedIds = new Set(
    depthEdges.flatMap(e => [e.source, e.target])
  );

  // Break any remaining cycles so the longest-path relaxation below converges.
  const backEdges = findBackEdges(depthEdges, allConnectedIds);
  const acyclicEdges = depthEdges.filter(
    e => !backEdges.has(`${e.source}:${e.target}`)
  );

  // Bellman-Ford: compute longest-path depth for each connected node.
  const initialDepth = new Map(
    Array.from(allConnectedIds, id => [id, 0] as const)
  );

  // Bellman-Ford relaxation pass. Reads and writes `next` within the same
  // iteration — this cascading is intentional: later edges see depths set by
  // earlier edges in the same pass, which makes convergence faster (fewer
  // total passes needed). The input `depthMap` is never mutated.
  const relaxOnce = (depthMap: Map<string, number>) => {
    const next = new Map(depthMap);
    const changed = acyclicEdges.reduce((hasChanged, edge) => {
      const sd = next.get(edge.source) ?? 0;
      const td = next.get(edge.target) ?? 0;
      if (sd + 1 > td) {
        next.set(edge.target, sd + 1);
        return true;
      }
      return hasChanged;
    }, false);
    return { next, changed };
  };

  const relaxUntilStable = (
    depthMap: Map<string, number>,
    remaining: number
  ): Map<string, number> => {
    const { next, changed } = relaxOnce(depthMap);
    if (!changed || remaining <= 0) return next;
    return relaxUntilStable(next, remaining - 1);
  };

  const rawDepth = relaxUntilStable(initialDepth, allConnectedIds.size);

  // A DAG's longest path visits at most (nodes - 1) edges, so depth can never
  // legitimately exceed that. Cycle-breaking above already guarantees a DAG;
  // this clamp is a safety net so that should any cycle ever slip through,
  // nodes pile into the final column rather than flying off-screen. Clamping
  // per-node (not just the max) keeps every node in a real column — a
  // max-only clamp would orphan over-deep nodes out of the column arrays.
  const depthBound = Math.max(0, allConnectedIds.size - 1);
  const depth = new Map(
    Array.from(rawDepth, ([id, d]) => [id, Math.min(d, depthBound)] as const)
  );

  // Group field and hook IDs into columns by depth.
  const maxNodeDepth = Math.max(0, ...depth.values());

  const connectedFieldIds = new Set(
    Array.from(allConnectedIds).filter(id => !hookIds.has(id))
  );
  const connectedHookIds = new Set(
    Array.from(allConnectedIds).filter(id => hookIds.has(id))
  );

  const fieldColumns: string[][] = Array.from(
    { length: maxNodeDepth + 1 },
    (_, colIdx) =>
      Array.from(connectedFieldIds).filter(
        id => (depth.get(id) ?? 0) === colIdx
      )
  );

  const hookColumns: string[][] = Array.from(
    { length: maxNodeDepth + 1 },
    (_, colIdx) =>
      Array.from(connectedHookIds).filter(id => (depth.get(id) ?? 0) === colIdx)
  );

  // Unconnected fields go into column 0 (after connected ones).
  const unconnectedFieldIds = fields
    .filter(f => !allConnectedIds.has(f.id))
    .map(f => f.id);

  // Unconnected hooks go into column 0.
  const unconnectedHookIds = hooks
    .filter(h => !allConnectedIds.has(h.id))
    .map(h => h.id);

  const finalFieldColumns = fieldColumns.map((col, i) =>
    i === 0 ? [...col, ...unconnectedFieldIds] : col
  );
  const finalHookColumns = hookColumns.map((col, i) =>
    i === 0 ? [...unconnectedHookIds, ...col] : col
  );

  // --- Top band: hooks (bottom-aligned so connected hooks sit near fields) ---
  const HOOK_ROW_HEIGHT = HOOK_NODE_HEIGHT + (ROW_HEIGHT - NODE_HEIGHT);
  const maxHookRows = Math.max(0, ...finalHookColumns.map(col => col.length));

  const hookNodes = finalHookColumns.flatMap((col, colIdx) => {
    const topOffset = (maxHookRows - col.length) * HOOK_ROW_HEIGHT;
    return col.flatMap<Node<ConfigMapNodeData>>((id, hookRowIdx) => {
      const hook = hookMap.get(id);
      // hook should always exist since finalHookColumns is built from hook IDs.
      // Skip silently if missing rather than fabricating data (e.g. hookId: 0
      // would generate a bogus URL).
      if (!hook) return [];
      return [
        {
          id,
          type: 'hook',
          position: {
            x: colIdx * COLUMN_WIDTH,
            y: topOffset + hookRowIdx * HOOK_ROW_HEIGHT,
          },
          data: {
            label: hook.name,
            hookId: hook.hookId,
            hookType: hook.hookType,
            active: hook.active,
            dependencies: hook.dependencies,
            targets: hook.targets,
            nodeKind: 'hook',
          },
        },
      ];
    });
  });
  const hookBandHeight = maxHookRows > 0 ? maxHookRows * HOOK_ROW_HEIGHT : 0;

  // --- Bottom band: fields (offset below hooks + gap) ---
  const fieldBandY = hookBandHeight > 0 ? hookBandHeight + BAND_GAP : 0;

  const fieldNodes = finalFieldColumns.flatMap((col, colIdx) =>
    col.map<Node<ConfigMapNodeData>>((id, rowIdx) => {
      const field = fieldMap.get(id);
      return {
        id,
        type: 'field',
        position: {
          x: colIdx * COLUMN_WIDTH,
          y: fieldBandY + rowIdx * ROW_HEIGHT,
        },
        data: {
          label: field?.id ?? id,
          fieldType: field?.type ?? 'external',
          dependencies: field?.dependencies ?? [],
          nodeKind: 'field',
        },
      };
    })
  );

  // Rules go into a dedicated column after all field/hook columns,
  // vertically centered around the hook/field junction (fieldBandY).
  const ruleColIdx = maxNodeDepth + 1;
  const rulesBlockHeight = rules.length * ROW_HEIGHT;
  const ruleStartY = fieldBandY - rulesBlockHeight / 2;

  const ruleNodes = rules.map<Node<ConfigMapNodeData>>((rule, rowIdx) => ({
    id: rule.id,
    type: 'rule',
    position: {
      x: ruleColIdx * COLUMN_WIDTH,
      y: ruleStartY + rowIdx * ROW_HEIGHT,
    },
    data: {
      label: rule.name,
      ruleId: rule.ruleId,
      enabled: rule.enabled,
      dependencies: rule.dependencies ?? [],
      nodeKind: 'rule',
    },
  }));

  return {
    nodes: [...fieldNodes, ...hookNodes, ...ruleNodes],
    fieldBandY,
  };
};
